import { AddToBag } from "@components/Icons"
import { useIntl } from "@components/Intl"
import IntlLink, { IntlLinkProps } from "@components/Intl/IntlLink"
import { Box } from "@components/Layout"
import { LoadingBar } from "@components/LoadingBar"
import { useNotificationsUpdate } from "@components/Notifications"
import { Label, TitleS } from "@components/Typography"
import formatPrice from "@lib/formatPrice"
import { useAddArticleToCart } from "@lib/hooks/use-add-article-to-cart"
import React from "react"
import * as S from "./elements"
import { dataLayerAddToCart } from "@lib/data-layer-events/add-to-cart"

interface Props extends IntlLinkProps {
  image?: string
  title: string
  price: number | null
  isLoading?: boolean
  id: string
  component: string | null
  size: string | null
}

const ProductCard: React.FC<Props> = ({
  href,
  image,
  as,
  title,
  id,
  component,
  price,
  size = null,
  isLoading = false,
}) => {
  const { messages } = useIntl()
  const { mutateAsync, isLoading: isAddToCartLoading } = useAddArticleToCart()
  const { addNotification } = useNotificationsUpdate()

  const addToCart = async () => {
    // price & component (type) are not mandatory in Articles_articles_items, but are in addArticle
    // this boolean ensures you can only add a product when its props comply with the types of addArticle
    if (price && component) {
      await mutateAsync({
        articleId: id,
      })
      dataLayerAddToCart({
        id,
        name: title,
        price,
      })
      addNotification({
        type: "success",
        text: `${title} ${messages.notifications.addedToCart}`,
      })
    }
  }

  const inner = (
    <S.Anchor
      href={typeof as === "string" ? as : undefined}
      title={`${messages.global.view} ${title}`}
      isLoading={isLoading}
    >
      {!isLoading && image && (
        <S.Image
          src={image}
          alt="Interchangeable jewelry"
          size={component === "stone" ? size : null}
        />
      )}
      <S.Content>
        {isLoading ? (
          <>
            <LoadingBar height="1.75rem" mb="1rem" width="80%" />
            <LoadingBar height="1rem" mb="0.25rem" width="40%" />
          </>
        ) : (
          <>
            <TitleS>{title}</TitleS>
            <Label style={{ marginTop: "0.5rem" }}>
              {price && formatPrice(price)}
            </Label>
          </>
        )}
      </S.Content>
    </S.Anchor>
  )
  return isLoading ? (
    inner
  ) : (
    <Box position="relative">
      <IntlLink href={href} as={as}>
        {inner}
      </IntlLink>
      {price && component && (
        <S.AddItem onClick={addToCart} isLoading={isAddToCartLoading}>
          <AddToBag />
        </S.AddItem>
      )}
    </Box>
  )
}

export default ProductCard
